import useChatStore from "@ext/agent/components/store/ChatStore";
import type { AgentDraftAttachment } from "@ext/agent/components/types/chat";
import { serializeAttachments } from "@ext/agent/components/utils/serializeAttachments";
import { agentConfig } from "@ext/agent/core/agentConfig";
import { AgentAttachmentStore } from "@ext/agent/core/attachmentStore";
import t from "@ext/localization/locale/translate";
import { type Dispatch, type SetStateAction, useCallback, useRef, useState } from "react";

const getAttachmentError = (file: File): string | null => {
	try {
		AgentAttachmentStore.assertAttachmentExtension(file.name);
	} catch {
		return t("agent.input.file-invalid-format");
	}
	if (file.size > agentConfig.maxAttachmentBytes) {
		return t("agent.input.file-too-large").replace("{{maxSizeMb}}", String(agentConfig.maxAttachmentMb));
	}
	return null;
};

export const useAttachFiles = (
	attachments: AgentDraftAttachment[],
	onAttachmentsChange: Dispatch<SetStateAction<AgentDraftAttachment[]>>,
) => {
	const [fileError, setFileError] = useState<string | null>(null);
	const pendingCountRef = useRef(0);

	const attachFiles = useCallback(
		(files: File[]) => {
			const errors = files.map(getAttachmentError);
			const valid = files.filter((_, i) => !errors[i]);
			const freeSlots = agentConfig.maxAttachments - attachments.length - pendingCountRef.current;
			const accepted = valid.slice(0, Math.max(freeSlots, 0));
			const limitError =
				accepted.length < valid.length
					? t("agent.input.too-many-files").replace("{{max}}", String(agentConfig.maxAttachments))
					: null;
			setFileError(errors.find(Boolean) ?? limitError);
			if (!accepted.length) return;

			const sessionId = useChatStore.getState().activeSessionId;
			pendingCountRef.current += accepted.length;
			void serializeAttachments(accepted)
				.then((serialized) => {
					if (useChatStore.getState().activeSessionId !== sessionId) return;
					onAttachmentsChange((prev) => [...prev, ...serialized]);
				})
				.finally(() => {
					pendingCountRef.current -= accepted.length;
				});
		},
		[attachments.length, onAttachmentsChange],
	);

	const isAttaching = useCallback(() => pendingCountRef.current > 0, []);

	return { attachFiles, isAttaching, fileError, setFileError };
};
